import type { LucideIcon } from 'lucide-react';
import {
  AlertTriangle,
  CheckCircle2,
  XCircle,
} from 'lucide-react';

import type {
  StudioPreflightCheckCategory,
  StudioPreflightCheckFixLink,
  StudioPreflightCheckStatus,
  StudioPreflightCheckSummary,
} from '~entities/studios/model/preflight-check.types';

import {
  PREFLIGHT_CHECK_ID,
  PREFLIGHT_CHECK_STATUS,
} from './preflight-check.types';

export const preflightStatusConfig: Record<
  StudioPreflightCheckStatus,
  {
    Icon: LucideIcon;
    iconClassName: string;
    rowClassName: string;
  }
> = {
  pass: {
    Icon: CheckCircle2,
    iconClassName: 'text-green-600',
    rowClassName: 'border-green-100 bg-green-50/60',
  },
  warn: {
    Icon: AlertTriangle,
    iconClassName: 'text-amber-600',
    rowClassName: 'border-amber-100 bg-amber-50/60',
  },
  fail: {
    Icon: XCircle,
    iconClassName: 'text-red-600',
    rowClassName: 'border-red-100 bg-red-50/60',
  },
};

export const preflightFixPathLabels: Record<string, string> = {
  '/payment/payment-integration': 'Payment integration',
  '/integrations': 'Integrations',
  '/payment/transaction-fee-settings': 'Transaction fee settings',
  '/payment/payment-settings': 'Payment settings',
  '/studio-profile': 'Studio profile',
  '/parent-form-config': 'Parent registration form',
  '/public-page-link': 'Public schedule page',
  '/category-config/locations': 'Locations',
  '/category-config/instructors': 'Instructors',
  '/email-settings': 'Email settings',
  '/makeup-classes': 'Makeup classes',
  '/reminders': 'Reminders',
  '/sessions': 'Sessions',
  '/classes': 'Classes',
  '/events': 'Events',
  '/payment/hourly-tuition/settings': 'Hourly tuition settings',
};

export function getVisiblePreflightChecks(
  checks: StudioPreflightCheckCategory['checks'],
): StudioPreflightCheckCategory['checks'] {
  try {
    return checks.filter(
      (check) => check.id !== PREFLIGHT_CHECK_ID.EVENT_GHL_CALENDAR,
    );
  } catch (error) {
    console.error('Failed to filter setup checklist items', { error });
    throw error;
  }
}

export function shouldExpandPreflightCategory(
  checks: StudioPreflightCheckCategory['checks'],
): boolean {
  try {
    return getVisiblePreflightChecks(checks).some(
      (check) =>
        check.status === PREFLIGHT_CHECK_STATUS.FAIL ||
        check.status === PREFLIGHT_CHECK_STATUS.WARN,
    );
  } catch (error) {
    console.error('Failed to decide setup checklist section state', { error });
    throw error;
  }
}

export function getVisiblePreflightSummary(
  categories: StudioPreflightCheckCategory[],
): StudioPreflightCheckSummary {
  try {
    const summary: StudioPreflightCheckSummary = {
      pass: 0,
      warn: 0,
      fail: 0,
    };

    for (const category of categories) {
      const visibleChecks = getVisiblePreflightChecks(category.checks);
      for (const check of visibleChecks) {
        summary[check.status] += 1;
      }
    }

    return summary;
  } catch (error) {
    console.error('Failed to count visible setup checklist items', { error });
    throw error;
  }
}

export function getPreflightFixLinkKey(
  checkId: string,
  link: StudioPreflightCheckFixLink,
): string {
  try {
    const paramValues = Object.values(link.params ?? {}).join('-');
    return `${checkId}-${link.to}-${paramValues || link.label}`;
  } catch (error) {
    throw error;
  }
}

export function getPreflightCheckTitleLink(check: {
  status: StudioPreflightCheckStatus;
  fixPath?: string;
  fixLinks?: StudioPreflightCheckFixLink[];
}): string | null {
  try {
    if (
      check.status !== PREFLIGHT_CHECK_STATUS.PASS ||
      !check.fixPath ||
      (check.fixLinks && check.fixLinks.length > 0)
    ) {
      return null;
    }
    return check.fixPath;
  } catch (error) {
    console.error('Failed to resolve setup checklist title link', {
      check,
      error,
    });
    throw error;
  }
}

export function getPreflightFixPathLabel(
  fixPath: string,
  status: StudioPreflightCheckStatus,
  checkId?: string,
): string {
  try {
    const destination =
      checkId === PREFLIGHT_CHECK_ID.TIMEZONE
        ? 'Settings'
        : (preflightFixPathLabels[fixPath] ?? fixPath);
    if (status === PREFLIGHT_CHECK_STATUS.PASS) {
      return `View ${destination}`;
    }
    return `Fix in ${destination}`;
  } catch (error) {
    throw error;
  }
}
